// A natural person is never a group label.
//
// Several group writers name a group after its controlling HOLDER: the Commission E loader
// (`group_name` of a control / shareholding edge — for SK, EE, LV, RO and GR that holder is
// usually a beneficial owner read off a register), the group-candidate promoter (labels voted from
// `entity_group_link.detail`, i.e. parent-name strings) and the Sunday economic-group recompute
// (clusters on the `corporate_parent` string). When the holder is a person, the group label became
// that person's full name, and it reached client surfaces (Consolidators, group panels). 2026-10-19
// audit: 180 such labels on ~420 entities.
//
// The replacement is deterministic and depends only on the old label, so every writer — whatever
// member set it happens to see — produces the SAME new label and a group is never forked in two.
// It carries no name: `SK: Private-owner group 3f9a2c`. Control stays where it is
// (`economic_group_control = founder`), so the fact "person-controlled" is kept without the person.
//
// The shape test is deliberately conservative AND it is only applied where the evidence itself is
// person-shaped (a holder edge with no registry id, a corporate_parent cluster of individually
// controlled companies). Brand labels named after a founder (Day Lewis, Louis Nielsen) arrive as
// brand / curated edges and are never touched.

import {
  READABLE_PRIVATE_OWNER_GROUP_LABELS,
  REVIEWED_NOT_PERSON_GROUP_LABEL_KEYS,
  REVIEWED_PERSON_GROUP_LABEL_KEYS,
} from './data/person-group-label-keys.js';

const LEGAL =
  /\b(s\.?\s?l\.?u?|s\.?\s?a\.?u?|lda|sas|sasu|sarl|eurl|sci|selarl|selas|spfpl|snc|srl|spa|scarl|gmbh|ggmbh|ag|kg|ohg|e\.?\s?v|ug|b\.?v|nv|vof|as|asa|ab|oy|oyj|aps|a\/s|sia|uab|ou|oü|kft|zrt|nyrt|s\.?r\.?o|a\.?s|z\.?u|o\.?p\.?s|n\.?o|d\.?o\.?o|sp\.?\s?z\s?o\.?o|ltd|limited|llp|plc|lp|inc|corp|corporation|co|ike|ae|epe|ead|eood|ood|asbl|vzw|bvba|cvba|slp|scp|cb|sc)\b\.?/i;
const BUSINESS =
  /(care|financ|gestion|participation|conseil|d[eé]veloppement|expansion|compa|prestation|location|vision|retail|trading|assets|leisure|holdco|topco|bidco|midco|newco|propco|opco|issuerco|group|groep|groupe|gruppe|gruppo|grupo|grupa|home|health|sant[eé]|salud|sa[uú]de|trust|house|lodge|court|hall|living|servic|senior|clinic|klinik|clinique|cl[ií]nica|dental|dent|tann|zahn|pharm|farmac|apothe|apotek|aptek|lekarn|l[eé]k[aá]r|chemist|optic|optik|optique|[oó]ptica|audio|logoped|fisio|physio|vet|surgery|medic|m[eé]dic|arzt|praxis|cabinet|consult|labor|imag|diagnos|centr|center|zentrum|residen|villa|park|garden|manor|nursing|pflege|soins|domov|dom |lar |vita|life|well|holding|invest|capital|partner|propert|estate|manage|enterpris|associat|venture|solution|global|international|europe|council|county|city|borough|society|mission|church|diocese|institut|hospital|spital|zorg|omsorg|hoiva|family|famil|sons\b|\bson\b|brothers|bros|mutual|mutuel|fund|fond|stift|verein|union|federation|national|regional|kapital|team|haigla|hambaravi|kliinik)/i;
const TITLE =
  /^(mr|mrs|ms|miss|mx|dr|prof|sir|dame|lady|lord|rev|mudr|mddr|phdr|ing|mgr|doc|bc)\.?$/i;
const PARTICLE = /^(de|da|del|della|di|van|von|der|den|le|la|du|dos|das|ter|ten|af|zu|mac|mc|o')$/i;

function stripLabel(label: string): string {
  return label
    .replace(/^[A-Z]{2}:\s*/, '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .trim();
}

/** True when a group label is shaped like one or two natural persons' names ("Jane Doe",
 *  "R & A Nagra", "DOE JANE", "Mr B and Mr S Doe") and carries no legal-form or business word. */
export function looksLikePersonName(label: string): boolean {
  const raw = stripLabel(label);
  if (!raw || /\d/.test(raw) || LEGAL.test(raw) || BUSINESS.test(raw)) return false;
  const parts = raw
    .split(/\s*(?:&|\band\b|\bund\b|\bet\b|\by\b|\bi\b|,|\/)\s*/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const words = parts.flatMap((p) => p.split(/\s+/)).filter((w) => !TITLE.test(w));
  if (words.length < 2 || words.length > 8) return false;
  return parts.every((p) => {
    const ws = p.split(/\s+/).filter((w) => !TITLE.test(w));
    return (
      ws.length >= 1 &&
      ws.length <= 4 &&
      ws.every(
        (w) => PARTICLE.test(w) || /^\p{Lu}(\.|[\p{L}'’.-]*)$/u.test(w), // Name, NAME, initial "A" / "A."
      )
    );
  });
}

function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** A stable, name-free key for a group label (case, accents and spacing folded). The reviewed
 *  denylist below is stored as these keys so no person's name is committed to the codebase. */
export function personLabelKey(label: string): string {
  return fnv1a(
    label
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim(),
  );
}

const REVIEWED = new Set<string>(REVIEWED_PERSON_GROUP_LABEL_KEYS);

/** True for a label a human reviewed and found to be a natural person (2026-10-19 audit, 180 labels). */
export function isReviewedPersonLabel(label: string): boolean {
  return REVIEWED.has(personLabelKey(label));
}

/** True for a label the 2026-10-19 review read and judged not a person (audit skips it). */
export function isReviewedNotPersonLabel(label: string): boolean {
  return REVIEWED_NOT.has(personLabelKey(label));
}
const REVIEWED_NOT = new Set<string>(REVIEWED_NOT_PERSON_GROUP_LABEL_KEYS);

/** The person-free replacement for a person-named group label. Deterministic in the old label: the
 *  readable care-group name when one was chosen (READABLE_PRIVATE_OWNER_GROUP_LABELS), else
 *  '<prefix>Private-owner group <6 hex>'. Every writer calls this, so all land on the same value. */
export function privateOwnerGroupLabel(label: string): string {
  const key = personLabelKey(label);
  const readable = READABLE_PRIVATE_OWNER_GROUP_LABELS[key];
  if (readable) return readable;
  const prefix = /^[A-Z]{2}:\s*/.exec(label)?.[0] ?? '';
  return `${prefix}Private-owner group ${key.slice(0, 6)}`;
}

/** The readable name for a generic 'Private-owner group <6 hex>' label, if one was chosen. */
export function readablePrivateOwnerLabel(genericLabel: string): string | null {
  const hex = /Private-owner group ([0-9a-f]{6})$/.exec(genericLabel)?.[1];
  if (!hex) return null;
  const hit = Object.keys(READABLE_PRIVATE_OWNER_GROUP_LABELS).filter((k) => k.startsWith(hex));
  return hit.length === 1 ? READABLE_PRIVATE_OWNER_GROUP_LABELS[hit[0]!]! : null;
}

/** What a writer stores. A reviewed person label is ALWAYS replaced (so no writer can re-mint
 *  it). Beyond that, a label is replaced only when the writer has person-shaped evidence (a holder
 *  edge with no registry id in a beneficial-owner market) AND the label looks like a name — the
 *  shape test alone is far too broad to run unattended (it flags ~3,000 company labels whose
 *  legal-form suffix was stripped), so every other writer passes personEvidence: false and new
 *  suspects go to scripts/audit-person-group-labels.mjs for a human. */
export function guardGroupLabel(label: string, opts: { personEvidence: boolean }): string {
  if (isReviewedPersonLabel(label)) return privateOwnerGroupLabel(label);
  return opts.personEvidence && looksLikePersonName(label) ? privateOwnerGroupLabel(label) : label;
}
